import type React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { ProgressAPI } from "generated/types";
import {
  ModuleType,
  type ProductModules,
  type ProductSkillLevel,
} from "models/product/ProductDetails";

import { buildUrlWithPathAndQueryParams, buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { AdaptiveTestWorkflowType } from "constants/exam-constants";
import { PRODUCT_PAGES_ROUTES, TEST_PAGE_ROUTES } from "constants/routes";

import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import { useMemo } from "react";
import { CssIcon } from "styles/helpers/layout";
import { BaseModuleCard } from "../BaseModuleCard/BaseModuleCard";
import {
  StyledSectionProgressList,
  StyledSectionProgressListItem,
} from "../WritingModuleCard/WritingModuleCard.styled";
import {
  StyledSubjectProgressList,
  StyledSubjectProgressListItem,
} from "./AdaptiveModuleCard.styled";

interface AdaptiveModuleCardProps {
  productId: number;
  moduleDetails: ProductModules;
  currentGoalLevel?: ProductSkillLevel | null;
  lastPracticedTopicStatus?: ProgressAPI.LastPracticedAdaptiveTopicStatus | null;
  currentProgressForAllGoalLevels?: ProgressAPI.CurrentProgressForGoalLevel[] | null;
}

export const AdaptiveModuleCard: React.FC<AdaptiveModuleCardProps> = ({
  productId,
  moduleDetails: module,
  currentGoalLevel = null,
  lastPracticedTopicStatus = null,
  currentProgressForAllGoalLevels = null,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("homepage");

  const userHasAlreadyPracticed = !!lastPracticedTopicStatus;
  const lastPracticedAdaptiveTopicStatus =
    lastPracticedTopicStatus as ProgressAPI.LastPracticedAdaptiveTopicStatus;

  // Calculate average progress in all sections
  const avgProgress = useMemo(() => {
    if (!currentProgressForAllGoalLevels || currentProgressForAllGoalLevels.length === 0) {
      return 0;
    }

    return (
      currentProgressForAllGoalLevels.reduce(
        (total, { avgProgress: progress }) => total + progress,
        0,
      ) / currentProgressForAllGoalLevels.length
    );
  }, [currentProgressForAllGoalLevels]);

  const userHasAlreadyFinished = avgProgress >= 100;

  // Render list of elements with progress per goal level
  const ProgressPerGoalLevel = useMemo(() => {
    if (!currentProgressForAllGoalLevels?.length) {
      return null;
    }

    return (
      <StyledSectionProgressList>
        {currentProgressForAllGoalLevels.map(
          ({ targetLevel, targetLevelName, avgProgress, subjects }) => {
            const isCompleted = avgProgress >= 100;
            return (
              <StyledSectionProgressListItem
                key={targetLevel}
                data-cy="goal-level-list-item"
                $isCompleted={isCompleted}
              >
                <CssIcon
                  data-cy="goal-level-status"
                  iconName={!isCompleted ? "far fa-circle" : "far fa-check-circle"}
                  iconSize="lg"
                />
                <span data-cy="goal-level-name">
                  {t("studentDashboard.currentProgressSection.moduleCard.targetLevel", {
                    goalLevel: targetLevelName,
                  })}
                </span>
                <span data-cy="goal-level-progress">{`${Math.round(avgProgress)}%`}</span>
                {/* -- For current goal level, show sublist with progres per subject -- */}
                {targetLevel === currentGoalLevel?.level && (
                  <StyledSubjectProgressList data-cy="detailed-progress-per-subject">
                    {subjects.map(({ id: subjectId, title: subjectTitle, progress }) => (
                      <StyledSubjectProgressListItem
                        key={subjectId}
                        data-cy="subject-list-item"
                        $isCompleted={progress >= 100}
                      >
                        <span data-cy="subject-name">{subjectTitle}</span>
                        <span data-cy="subject-progress">{`${Math.round(progress)}%`}</span>
                      </StyledSubjectProgressListItem>
                    ))}
                  </StyledSubjectProgressList>
                )}
              </StyledSectionProgressListItem>
            );
          },
        )}
      </StyledSectionProgressList>
    );
  }, [currentGoalLevel, currentProgressForAllGoalLevels]);

  // For adaptive modules, we need to check if user had chosen the mixed or default flow
  const isMixedAdaptiveWorkflowActive =
    module.type === ModuleType.ADAPTIVE &&
    lastPracticedAdaptiveTopicStatus &&
    lastPracticedAdaptiveTopicStatus.selectedWorkflow === AdaptiveTestWorkflowType.MIXED;

  const onCardClick = () => {
    if (!userHasAlreadyPracticed || userHasAlreadyFinished) {
      navigate(
        buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS, {
          productId,
          moduleId: module.id,
        }),
      );
      return;
    }

    const { topicId, subjectId } = lastPracticedTopicStatus;

    // Track continue event in Metabase
    trackPlatformEvent({
      module: STUDENT_MODULES.DASHBOARD,
      itemId: isMixedAdaptiveWorkflowActive
        ? PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_CONTINUE_PRACTICING_MIX
        : PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_CONTINUE_PRACTICING_TOPIC,
      value: JSON.stringify({
        productId,
        moduleId: module.id,
        subjectId,
        ...(!isMixedAdaptiveWorkflowActive ? { topicId } : {}),
      }),
    });

    // Navigate to adaptive practiced with the last selected workflow
    // If user had chosen sequencial workflow, we will redirect to specific topic
    const isTopicCompleted = lastPracticedAdaptiveTopicStatus?.progressInCurrentLevel > 99;
    navigate(
      buildUrlWithPathAndQueryParams(
        TEST_PAGE_ROUTES.ADAPTIVE_PRACTICE_TEST,
        { productId, moduleId: module.id, subjectId },
        isMixedAdaptiveWorkflowActive
          ? { workflow: AdaptiveTestWorkflowType.MIXED as string }
          : isTopicCompleted
            ? undefined
            : { topicId },
      ),
    );
  };

  return (
    <BaseModuleCard
      moduleId={module.id}
      moduleTitle={module.title}
      moduleDescription={module.description || ""}
      actionButtonLabel={
        userHasAlreadyFinished
          ? t("studentDashboard.currentProgressSection.moduleCard.reviewButton")
          : userHasAlreadyPracticed
            ? t("studentDashboard.currentProgressSection.moduleCard.continueButton")
            : t("studentDashboard.currentProgressSection.moduleCard.startButton")
      }
      onCardClick={onCardClick}
      actionButtonVariant={userHasAlreadyFinished ? "success" : "primary"}
      onActionButtonClick={onCardClick}
      avgProgress={Math.ceil(avgProgress)}
      extraCardContent={ProgressPerGoalLevel}
    />
  );
};
